import { useState, useEffect } from "react";

const C = {
  bg: "#ffffff",
  border: "rgba(0,0,0,0.10)",
  text: "#1a1a1a",
  textMuted: "#6b6b6b",
  red: "#ef4444",
  redBg: "#fef2f2",
};

const Icon = ({ name, size = 14, style = {} }) => (
  <i
    className={`ti ti-${name}`}
    style={{ fontSize: size, lineHeight: 1, ...style }}
    aria-hidden
  />
);

export default function DeleteModal({ open, site, onClose, onConfirm }) {
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const siteName = site?.name || site?.slug || "";
  const expectedText = `Delete ${siteName}`;
  const isValid = confirmText === expectedText;

  // reset state whenever the modal opens for a new site
  useEffect(() => {
    if (open) {
      setConfirmText("");
      setDeleting(false);
    }
  }, [open, site?.id]);

  // close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open || !site) return null;

  const handleDelete = async () => {
    if (!isValid || deleting) return;
    setDeleting(true);
    try {
      await onConfirm(site.id);
      onClose();
    } catch (err) {
      setDeleting(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 400,
          maxWidth: "90vw",
          background: C.bg,
          borderRadius: 12,
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          overflow: "hidden",
        }}
      >
        {/* Body */}
        <div style={{ padding: "24px 24px 20px" }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, color: C.text, margin: 0, marginBottom: 10 }}>
            Delete website?
          </h2>
          <p style={{ fontSize: 13, color: C.textMuted, lineHeight: 1.5, margin: 0, marginBottom: 18 }}>
            Are you sure you want to delete <strong style={{ color: C.text }}>{siteName}</strong>?
            This will permanently delete the website, all of its data, and any associated resources.
          </p>

          <label style={{ fontSize: 13, color: C.textMuted, display: "block", marginBottom: 8 }}>
            Type <strong style={{ color: C.text }}>{expectedText}</strong> to confirm
          </label>
          <input
            autoFocus
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={expectedText}
            onKeyDown={(e) => e.key === "Enter" && handleDelete()}
            style={{
              width: "100%",
              padding: "9px 12px",
              border: `1.5px solid ${confirmText ? (isValid ? C.border : "rgba(239,68,68,0.4)") : C.border}`,
              borderRadius: 8,
              fontSize: 13,
              color: C.text,
              outline: "none",
              fontFamily: "inherit",
              boxSizing: "border-box",
              transition: "border-color 0.15s",
            }}
          />
        </div>

        {/* Footer */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            padding: "14px 24px",
            borderTop: `0.5px solid ${C.border}`,
            background: "#fafaf9",
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "8px 16px",
              border: `0.5px solid ${C.border}`,
              borderRadius: 7,
              background: "#fff",
              color: C.text,
              fontSize: 13,
              fontWeight: 500,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={!isValid || deleting}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 16px",
              border: "none",
              borderRadius: 7,
              background: isValid ? C.redBg : "#f3f4f6",
              color: isValid ? C.red : "#9ca3af",
              fontSize: 13,
              fontWeight: 500,
              cursor: isValid && !deleting ? "pointer" : "default",
              fontFamily: "inherit",
              transition: "all 0.15s",
            }}
          >
            {deleting && <Icon name="loader-2" size={13} style={{ animation: "spin 1s linear infinite" }} />}
            {deleting ? "Deleting" : "Delete"}
          </button>
        </div>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}